import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'
import { activeTicketsOfExpeditions } from '../../../helpers'

import { Button, Page, Spinner } from '../../../components'
import { images } from '../../../images'
import {
  AuthenticatedRoutable,
  IActiveTicketsByExpedition,
  IAvailability,
  IPublicTicket,
  IStop,
  IUser
} from '../../../models'
import { urls } from '../../../routing'
import { LiteralsService, SuggestionService } from '../../../services'
import {
  setDestinationAvailabilityAction,
  setDisplayedActiveTicketsAction,
  setReturnDestinationAvailabilityAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from '../styles'
import { Card } from './Card'
import StyledSection from './styles'
import { IFindResultAvailabilities } from '../../../models/find-result-availabilities'
import { appsStyleSelected } from '../../../helpers/app'
import { ReservationInstructions } from '../../../containers/NewReservation/Instructions'
import theme from '../../../theme'
import { Application } from '../../../config'
import { AnyAction } from 'redux'

const { isIframe } = Application

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  pickedOrigin?: IStop
  pickedDestination?: IStop
  pickedDate: Date
  routeOptionsAndFailures: IFindResultAvailabilities
  returnRouteOptionsAndFailures: IFindResultAvailabilities
  isFetchingRouteOptions: boolean
  user: IUser
  isRoundTrip: boolean
  pickedAvailability: IAvailability
  returnPickedAvailability: IAvailability
}

interface IState {
  showOptions: boolean
  showReturnOptions: boolean
  activeTicketsByExpedition: IActiveTicketsByExpedition
}

class RouteOptionsStep extends AuthenticatedRoutable<IProps, IState> {

  constructor(props: IProps) {
    super(props)

    this.state = {
      showOptions: false,
      showReturnOptions: false,
      activeTicketsByExpedition: {}
    }
  }

  componentDidMount(): void {
    const { user } = this.props
    if (!user.id) super.componentDidMount()
    this.fetchActiveTickets()
  }

  componentDidUpdate(previousProps: IProps): void {
    const { routeOptionsAndFailures, returnRouteOptionsAndFailures } = this.props
    if (
      previousProps.routeOptionsAndFailures !== routeOptionsAndFailures ||
      previousProps.returnRouteOptionsAndFailures !== returnRouteOptionsAndFailures
    ) this.fetchActiveTickets()
  }

  private fetchActiveTickets = async () => {
    const { routeOptionsAndFailures, returnRouteOptionsAndFailures } = this.props
    const availabilities = [
      ...(routeOptionsAndFailures?.availabilities ?? []),
      ...(returnRouteOptionsAndFailures?.availabilities ?? [])
    ]
    const activeTicketsByExpedition = await SuggestionService.getActiveTicketsByExpeditions(
      availabilities.map((availability) => availability.expeditionId)
    )
    if (
      routeOptionsAndFailures === this.props.routeOptionsAndFailures &&
      returnRouteOptionsAndFailures === this.props.returnRouteOptionsAndFailures
    ) this.setState({ activeTicketsByExpedition })
  }

  private activeTicketsOf(availability: IAvailability): IPublicTicket[] {
    return activeTicketsOfExpeditions(this.state.activeTicketsByExpedition, [availability.expeditionId])
  }

  private showActiveTickets = (activeTickets: IPublicTicket[]) => {
    this.props.dispatch(setDisplayedActiveTicketsAction(activeTickets))
  }

  private get noOptions(): ReactNode {
    return (
      <div className="no-options">
        <img src={ images['noOptions'] } alt="No options" />
        <h3>
          { LiteralsService.get('noResults', true)}
        </h3>
        <p>
          { LiteralsService.get('noResultsExplanation', true)}
        </p>
        <Button
          title={ LiteralsService.get('modifyRoute') }
          onPress={ () => this.props.history.push(urls.newReservationDateStep, this.props.history.location.state) }
          filled
          upper
        />
      </div>
    )
  }

  private routeOptionsDisplay(isReturn: boolean): ReactNode {
    const { routeOptionsAndFailures, pickedOrigin, pickedDestination, returnPickedAvailability, pickedAvailability } = this.props
    return (
      <>
        <div className='options-header'>
          <h3>
            { LiteralsService.get('moreOptions', true) }
          </h3>
        </div>
        <div>
          {
            routeOptionsAndFailures?.availabilities
            .slice(1)
            .map((routeOption, index) => {
              return (
                <Card
                  key={ `${ routeOption.id }-${ index }` }
                  isLast={ (index + 2) === routeOptionsAndFailures?.availabilities?.length }
                  routeOption={ routeOption }
                  pickedOriginBusStop={ pickedOrigin }
                  pickedDestinationBusStop={ pickedDestination }
                  onClick={ (availability) => this.onRouteOptionClick(availability, isReturn) }
                  selected={ isReturn ? (returnPickedAvailability && routeOption.id ===  returnPickedAvailability.id) : (pickedAvailability && routeOption.id === pickedAvailability.id) }
                  isSingleStop={ pickedOrigin?.serviceInfo?.singleStopReservations || pickedDestination?.serviceInfo?.singleStopReservations }
                  activeTickets={ this.activeTicketsOf(routeOption) }
                  onActiveTicketsClick={ this.showActiveTickets }
                />
              )
            })
          }
        </div>
      </>
    )
  }

  private optionsDisplay(isReturn: boolean): ReactNode {
    const { pickedOrigin, pickedDestination, pickedAvailability, returnPickedAvailability, routeOptionsAndFailures, returnRouteOptionsAndFailures, isRoundTrip } = this.props

    const recommendedAvailability = isReturn ? returnRouteOptionsAndFailures?.availabilities[0] : routeOptionsAndFailures?.availabilities[0]

    return (
      <section className="options">
        <div className='options-header'>
          <h3>
            { LiteralsService.get('recommended', true) }
          </h3>
          {
            isRoundTrip &&
            <div className='direction-container'>
              <img src={images.directionArrow} />
              <span> { LiteralsService.get(`directions.${isReturn ? 'return' : 'outbound'}Journey`).toUpperCase() } </span>
            </div>
          }
        </div>
        {
          recommendedAvailability &&
          <Card
            isLast={ true }
            routeOption={ recommendedAvailability }
            onClick={ (availability) => this.onRouteOptionClick(availability, isReturn) }
            pickedOriginBusStop={ isReturn ? pickedDestination : pickedOrigin }
            pickedDestinationBusStop={ isReturn ? pickedOrigin : pickedDestination }
            selected={ isReturn ? recommendedAvailability?.id === returnPickedAvailability?.id : recommendedAvailability?.id === pickedAvailability?.id }
            isSingleStop={ pickedOrigin?.serviceInfo?.singleStopReservations || pickedDestination?.serviceInfo?.singleStopReservations }
            activeTickets={ this.activeTicketsOf(recommendedAvailability) }
            onActiveTicketsClick={ this.showActiveTickets }
          />
        }
      </section>
    )
  }

  private moreOptionsDisplay(isReturn: boolean): ReactNode {
    const { showOptions, showReturnOptions } = this.state
    let show = isReturn ? showReturnOptions : showOptions
    return (
      <section className="options">
        { show && this.routeOptionsDisplay(isReturn) }
        <div className='show-more-container' onClick={ () => {
          if (isReturn) {
            this.setState({
              showReturnOptions: !showReturnOptions
            })
          } else {
            this.setState({
              showOptions: !showOptions
            })
          }
        }}>
          <img src={show ? images.up : images.down} alt='show-more-arrow' />
          <span className='link-action'>{ LiteralsService.get(show ? 'showLess' : 'showMore', true) }</span>
        </div>
      </section>
    )
  }

  private content(isReturn: boolean): ReactNode {
    const { isFetchingRouteOptions, routeOptionsAndFailures } = this.props

    if (isFetchingRouteOptions) return <Spinner />
    else if (!routeOptionsAndFailures?.availabilities?.length) return this.noOptions
    else return this.optionsDisplay(isReturn)
  }
  
  private get instructions(): ReactNode {
    return (
      <ReservationInstructions
        textTitle= { LiteralsService.get('tutorialChooseExpeditionTitle', true) }
        textContent= { LiteralsService.get('tutorialChooseExpeditionContent', true) }
        backgroundColor= { theme.colors.greyLight }
      />
    )
  }

  private get nextButton(): ReactNode {
    const { history } = this.props

    return (
      <Button
        title={ LiteralsService.get('next') }
        onPress={ () => history.push(urls.newReservationConfirmationStep, {
          userData: history.location.state?.userData,
          townId: history.location.state?.townId,
        }) }
        isDisabled={ this.isNextButtonDisabled() }
        styles={{
          boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
        }}
        filled
        upper
      />
    )
  }

  public render(): ReactNode {
    const { isRoundTrip, history } = this.props
    return (
      <Page className={`authenticated ${appsStyleSelected?.style}`}>
        <StyledArticle className={`split ${appsStyleSelected?.style}`}>
          <div>
            <StyledSection className='card-container'>
              <header>
                { !isIframe &&
                  <h3>
                    { LiteralsService.get('desiredRoute', true).toUpperCase() }
                  </h3>
                }
                { this.instructions }
              </header>
              { this.content(false) }
              { this.props.routeOptionsAndFailures?.availabilities?.length > 1 && this.moreOptionsDisplay(false) }
            </StyledSection>
            {
              isRoundTrip &&
              <StyledSection className='card-container'>
                { this.content(true) }
                { this.props.returnRouteOptionsAndFailures?.availabilities?.length > 1 && this.moreOptionsDisplay(true) }
              </StyledSection>
            }
          </div>
          <footer>
            <section className='buttons-wrapper'>
              { this.nextButton }
              <Button
                title={ LiteralsService.get('previous') }
                onPress={ () => history.push(urls.newReservationDateStep, {
                  userData: history.location.state?.userData,
                  townId: history.location.state?.townId,
                }) }
                styles={{
                  backgroundColor: theme.colors.white,
                  borderColor: theme.colors.primary,
                  borderWidth: 2,
                  boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
                }}
                upper
              />
            </section>
          </footer>
        </StyledArticle>
      </Page>
    )
  }

  private onRouteOptionClick = (availability: IAvailability, isReturn: boolean) => {
    const { dispatch } = this.props
    if (isReturn) dispatch(setReturnDestinationAvailabilityAction(availability))
    else dispatch(setDestinationAvailabilityAction(availability))
  }

  private isNextButtonDisabled = () => {
    const { isRoundTrip, pickedAvailability, returnPickedAvailability } = this.props

    if (isRoundTrip) {
      if (pickedAvailability && returnPickedAvailability) return false
      return true
    } else {
      if (pickedAvailability) return false
      return true
    }
  }
}

const mapStateToProps = ({ newReservation, userInfo }: IStoreState) => ({
  pickedDate: newReservation.pickedDate,
  routeOptionsAndFailures: newReservation.routeOptionsAndFailures,
  returnRouteOptionsAndFailures: newReservation.returnRouteOptionsAndFailures,
  pickedOrigin: newReservation.pickedOriginBusStop,
  pickedDestination: newReservation.pickedDestinationBusStop,
  isFetchingRouteOptions: newReservation.isFetchingRouteOptions,
  user: userInfo,
  isRoundTrip: newReservation.isRoundTrip,
  pickedAvailability: newReservation.pickedAvailability,
  returnPickedAvailability: newReservation.returnPickedAvailability
})

export default withRouter(connectRedux(mapStateToProps, RouteOptionsStep as any))
